import { createContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest, UserRole } from '../models/User';
import * as authService from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isHotelManager: boolean;
  userRole: UserRole | null;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  isAuthenticated: false,
  isHotelManager: false,
  userRole: null,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          setUser(storedUser);
          console.log("Stored user found:", storedUser);

          try {
            // Ensure API is configured before refreshing user data
            const selectedHotel = localStorage.getItem('selectedHotel');
            if (selectedHotel) {
              const hotel = JSON.parse(selectedHotel);
              const { updateApiForHotel } = await import('../services/api');
              updateApiForHotel(hotel.serverUrl, hotel.serverPort);
              console.log("API configured with stored hotel:", hotel.name);
            } else {
              // No selected hotel stored, need to run hotel discovery
              console.log("No selected hotel found, running hotel discovery...");
              try {
                const { getPublicHotelsList } = await import('../services/hotelService');
                const hotels = await getPublicHotelsList();
                console.log("Hotel discovery completed successfully, found hotels:", hotels.length);
              } catch (discoveryError) {
                console.error("Hotel discovery failed:", discoveryError);
                throw discoveryError;
              }
            }

            // Refresh user data
            const currentUser = await authService.getCurrentUser();
            console.log("Current user data refreshed:", currentUser);
            setUser(currentUser);
          } catch (error) {
            console.error('Failed to refresh user data:', error);

            // Don't logout for API refresh failures - keep the stored user
            console.log("API refresh failed, but keeping stored user authenticated");
          }
        } else {
          console.log("No stored user found");
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    setLoading(true);
    try {
      const loggedInUser = await authService.login(credentials);
      setUser(loggedInUser);
      console.log("User state updated successfully:", loggedInUser);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const isAuthenticated = !!user;

  // Check if user role is the HotelManager enum value (1)
  const isHotelManager = user?.role === UserRole.HotelManager;

  // Ensure role is properly set as enum type (backward compat for old tokens)
  if (user && typeof user.role === 'string') {
    if (user.role === 'HotelManager' || user.role === 'HotelAdmin' || user.role === 'Admin') {
      user.role = UserRole.HotelManager;
    } else if (user.role === 'HotelUser') {
      user.role = UserRole.HotelUser;
    }
  }

  // Get the user role directly from the user object
  const userRole = user?.role ?? null;

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, isAuthenticated, isHotelManager, userRole }}
    >
      {children}
    </AuthContext.Provider>
  );
};
